import { Flex, rem, Skeleton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getPersonBankAccounts } from '@dealstack/frontend/app/services/Person.service.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsCard/InvestorDetailsCard.tsx';
import { BankAccountTile } from '@dealstack/frontend/shared/components/BankAccountTile/BankAccountTile.tsx';
import { DetailsCardEmptyState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DetailsCardEmptyState.tsx';
import { useBankAccountLifecycleDebug } from '@dealstack/frontend/app/components/BankAccountLifecycleDebug/useBankAccountLifecycleDebug.tsx';

interface Props {
	personId: string | undefined;
}

export const BankAccountsCard = ({ personId }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { onInspect, drawer: lifecycleDebugDrawer } = useBankAccountLifecycleDebug();

	const bankAccountsQuery = useQuery({
		queryKey: [API_PATHS.PERSON_PERSONID_BANKACCOUNT_GET, personId],
		queryFn: () => getPersonBankAccounts(personId ?? ''),
		enabled: !!personId,
	});

	const accounts = bankAccountsQuery.data ?? [];

	if (bankAccountsQuery.isPending) {
		return <Skeleton height={rem(204)} radius="0.5rem" />;
	}

	return (
		<InvestorDetailsCard
			title={tMipManage('investors.details.bank_card.title')}
			count={accounts.length}
			action={{ kind: 'expand', defaultExpanded: !!accounts.length }}
			dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_BANK_CARD}
		>
			{accounts.length > 0 ? (
				<Flex direction="column" gap="0.25rem">
					{accounts.map((account) => (
						<BankAccountTile
							key={account.id}
							bankAccount={account}
							hasEditPermission={false}
							showCopyButton
							showStatusBadge
							onInspect={onInspect}
							withBorder
							tileTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_BANK_ACCOUNT}
							statusBadgeTestId={TEST_ELEMENT_IDS.CAP_BANK_ACCOUNT_TILE}
							copyBtnTestId={
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_BANK_ACCOUNT_COPY_BUTTON
							}
							testIdSuffix={account.id}
						/>
					))}
				</Flex>
			) : (
				<DetailsCardEmptyState message={tMipManage('investors.details.bank_card.empty')} />
			)}
			{lifecycleDebugDrawer}
		</InvestorDetailsCard>
	);
};
